<script setup lang="ts" generic="Item extends object, Headers extends string">
import type { MrDataTableHeader, DataTableGroupings } from '#imports';
// @todo idea: split vertical and horizontal extra slots if needed,
//  non-splitted slots should be in both vertical and horizontal

type TableStyle = 'horizontal' | 'vertical';
type OverflowBehavior = 'scroll' | 'multiline';
const {
    loading,
    headers,
    hideAppend,
    hideNoData,
    hidePrepend,
    itemClass = 'itemClass',
    fixedHeader,
    items,
    groupBy,
    mobileStyle = 'vertical',
    noDataText = 'اطلاعاتی یافت نشد.',
    overflowBehavior = 'scroll',
    desktopStyle = 'horizontal',
    'onClick:row': onClickRow,
} = defineProps<{
    noDataText?: string;
    itemClass?: string;
    headers: MrDataTableHeader<Headers>[];
    loading?: boolean;
    items: Item[];
    groupBy?: DataTableGroupings<Item>;
    hideNoData?: boolean;
    fixedHeader?: boolean;
    hideAppend?: boolean;
    hidePrepend?: boolean;
    desktopStyle?: TableStyle;
    mobileStyle?: TableStyle;
    overflowBehavior?: OverflowBehavior;
    // eslint-disable-next-line vue/prop-name-casing
    'onClick:row'?: (item: Item, index: number) => void;
}>();

const emit = defineEmits<{
    'click:row': [item: Item, index: number];
}>();
defineSlots<
    ItemsMainSlots &
        HeaderSlots &
        HeaderAppendSlots &
        HeaderPrependSlots &
        ItemsMainStaticSlots &
        ItemsAppendSlots &
        ItemsPrependSlots &
        StaticSlots
>();
const { isMobile } = useSize();

const horizontal = computed(() =>
    isMobile.value
        ? mobileStyle === 'horizontal'
        : desktopStyle === 'horizontal'
);

const slots = useSlots();
type ItemsMainSlots = {
    [key in Headers as `item.${key}`]: (props: {
        index: number;
        value: key extends keyof Item ? Item[key] : undefined;
        item: Item;
        header: MrDataTableHeader<key>;
        span: number;
    }) => any;
};

type HeaderSlots = {
    [key in Headers as `header.${key}`]: (props: {
        header: MrDataTableHeader<key>;
    }) => any;
};

type HeaderAppendSlots = {
    [key in Headers as `header.append.${key}`]: (props: {
        header: MrDataTableHeader<key>;
    }) => any;
};

type HeaderPrependSlots = {
    [key in Headers as `header.prepend.${key}`]: (props: {
        header: MrDataTableHeader<key>;
    }) => any;
};

type ItemsMainStaticSlots = {
    'item.data-table-index': (props: {
        index: number;
        value: number;
        item: Item;
        header: MrDataTableHeader<'data-table-index'>;
    }) => any;
};

type ItemsAppendSlots = {
    [key in Headers as `item.append.${key}`]: (props: {
        header: MrDataTableHeader<key>;
    }) => any;
};

type ItemsPrependSlots = {
    [key in Headers as `item.prepend.${key}`]: (props: {
        header: MrDataTableHeader<key>;
    }) => any;
};

type StaticSlots = {
    loading: (props: any) => any;
    'no-data': (props: any) => any;
    'row.append': () => any;
    'row.prepend': () => any;
    [key: string]: (props: any) => any;
};

const hasAppendSlot = computed(() => {
    if (hideAppend) {
        return false;
    }
    return !!appendHeaders.value.length || 'row.append' in slots;
});

const hasPrependSlot = computed(() => {
    if (hidePrepend) {
        return false;
    }
    return !!prependHeaders.value.length || 'row.prepend' in slots;
});

const indexedItems = computed<Item[]>(() =>
    items?.map((item: any, index) => {
        item['data-table-index'] = index + 1;
        return item;
    })
);
const normalizedHeaders = computed(() =>
    headers
        .filter(header => !toValue(header.disabled))
        .map(header => ({
            ...header,
            title: toValue(header.title),
        }))
);
const noData = computed(() => !items.length && !hideNoData);

const prependHeaders = computed(() => {
    const slotKeys = Object.keys(slots)
        .filter(key => key.startsWith('item.prepend'))
        .map(key => key.replace('item.prepend.', ''));
    return normalizedHeaders.value.filter(header =>
        slotKeys.includes(header.key)
    );
});

const appendHeaders = computed(() => {
    const slotKeys = Object.keys(slots)
        .filter(key => key.startsWith('item.append'))
        .map(key => key.replace('item.append.', ''));
    return normalizedHeaders.value.filter(header =>
        slotKeys.includes(header.key)
    );
});

function normalizeSpacing(spacing: number | string | undefined) {
    if (spacing === undefined) {
        return undefined;
    }
    if (typeof spacing === 'string' || spacing === 0) {
        return spacing;
    }
    return `${spacing}px`;
}

type ItemExtendedValue<T> = {
    data: T;
    span?: number;
    skip?: boolean;
};

type ExtendedValue<T> = {
    [key in keyof T]: ItemExtendedValue<T[key]>;
};

const mergedItems = computed(() => {
    const extendItem = (item: Item, skip = false) =>
        EnumClass.fromEntries<ExtendedValue<Item>>(
            EnumClass.create(item)
                .getEntries()
                .map(([key, value]) => [
                    key,
                    {
                        data: value,
                        skip: skip && groupBy?.columns.includes(key),
                    },
                ])
        );
    if (!groupBy) {
        return indexedItems.value.map(item => extendItem(item));
    }
    if (!indexedItems.value.length) {
        return [];
    }
    const { by } = groupBy;
    const keyFunction = typeof by === 'function' ? by : (row: Item) => row[by];
    let currentIndex = 0;
    let currentValue = keyFunction(indexedItems.value[0]);
    const result: ExtendedValue<Item>[] = [extendItem(indexedItems.value[0])];
    for (let i = 1; i < indexedItems.value.length; i++) {
        const compareValue = keyFunction(indexedItems.value[i]);
        if (compareValue === currentValue) {
            result.push(extendItem(indexedItems.value[i], true));
            for (const key of groupBy.columns) {
                result[currentIndex][key].span =
                    (result[currentIndex][key].span ?? 1) + 1;
            }
        } else {
            result.push(extendItem(indexedItems.value[i]));
            currentValue = compareValue;
            currentIndex = i;
        }
    }
    return result;
});

const root = ref<HTMLDivElement>();
defineExpose({ root });
</script>

<template>
    <div ref="root">
        <div
            v-if="horizontal"
            class="mr-data-table-root horizontal"
            :class="[overflowBehavior]"
        >
            <table class="table">
                <tr class="row-header" :class="{ fixed: fixedHeader }">
                    <th
                        v-for="header in normalizedHeaders"
                        :key="header.key"
                        class="header"
                        :class="[
                            header.align || 'center',
                            header.columnClass,
                            { sticky: header.fixed },
                        ]"
                        :style="{
                            width: normalizeSpacing(header.width),
                            minWidth: normalizeSpacing(header.minWidth),
                        }"
                    >
                        <slot :name="`header.${header.key as any}`" :header>
                            {{ header.title }}
                        </slot>
                    </th>
                </tr>
                <slot v-if="loading" name="loading">
                    <tr class="loading">
                        <td :colspan="headers.length">
                            <mr-loading />
                        </td>
                    </tr>
                </slot>
                <slot v-else-if="noData" name="no-data">
                    <tr>
                        <td :colspan="headers.length" class="center">
                            {{ noDataText }}
                        </td>
                    </tr>
                </slot>
                <template v-else>
                    <tr v-if="hasPrependSlot" class="row-data .prepend">
                        <slot name="row.prepend">
                            <td
                                v-for="header in normalizedHeaders"
                                :key="`prepend-${header.key}`"
                                class="data"
                                :class="[
                                    header.align || 'center',
                                    header.columnClass,
                                    { sticky: header.fixed, bordered: groupBy },
                                ]"
                                :style="{
                                    width: normalizeSpacing(header.width),
                                    minWidth: normalizeSpacing(header.minWidth),
                                }"
                            >
                                <slot
                                    :name="`item.prepend.${header.key as any}`"
                                />
                            </td>
                        </slot>
                    </tr>
                    <tr
                        v-for="(item, index) in mergedItems"
                        :key="index"
                        class="row-data"
                        :class="[
                            (item as any)[itemClass]?.data,
                            {
                                last:
                                    index === mergedItems.length - 1 &&
                                    !hasAppendSlot,
                                first: index === 0 && !hasPrependSlot,
                                clickable: !!onClickRow,
                            },
                        ]"
                        @click="emit('click:row', indexedItems[index], index)"
                    >
                        <td
                            v-for="header in normalizedHeaders.filter(
                                h => !(item as any)[h.key]?.skip
                            )"
                            :key="`${index}-${header.key}`"
                            class="data"
                            :class="[
                                header.align || 'center',
                                header.columnClass,
                                { sticky: header.fixed, bordered: groupBy },
                            ]"
                            :style="{
                                width: normalizeSpacing(header.width),
                                minWidth: normalizeSpacing(header.minWidth),
                            }"
                            :rowspan="(item as any)[header.key]?.span ?? 1"
                        >
                            <slot
                                :name="`item.${header.key as any}`"
                                :header
                                :value="(item as any)[header.key]?.data"
                                :item="indexedItems[index]"
                                :index
                                :span="(item as any)[header.key]?.span ?? 1"
                            >
                                {{ (item as any)[header.key]?.data }}
                            </slot>
                        </td>
                    </tr>
                    <tr v-if="hasAppendSlot" class="row-data append">
                        <slot name="row.append">
                            <td
                                v-for="header in normalizedHeaders"
                                :key="`append-${header.key}`"
                                class="data"
                                :class="[
                                    header.align || 'center',
                                    header.columnClass,
                                    { sticky: header.fixed, bordered: groupBy },
                                ]"
                                :style="{
                                    width: normalizeSpacing(header.width),
                                    minWidth: normalizeSpacing(header.minWidth),
                                }"
                            >
                                <slot
                                    :name="`item.append.${header.key as any}`"
                                    :header
                                />
                            </td>
                        </slot>
                    </tr>
                </template>
            </table>
        </div>
        <div v-if="!horizontal" class="mr-data-table-root vertical">
            <slot v-if="loading" name="loading">
                <mr-loading />
            </slot>
            <slot v-else-if="noData" name="no-data">
                <p class="no-data">{{ noDataText }}</p>
            </slot>
            <template v-else>
                <table v-if="hasPrependSlot" class="data-table prepend">
                    <slot name="row.prepend">
                        <tr
                            v-for="header in prependHeaders"
                            :key="`prepend-${header.key}`"
                            class="data-row"
                            :class="[
                                header.align || 'center',
                                header.columnClass,
                            ]"
                        >
                            <th class="data-title">
                                <slot
                                    :name="`header.prepend.${
                                        header.key as any
                                    }`"
                                    :header
                                >
                                    {{ header.title }}
                                </slot>
                            </th>
                            <td class="data">
                                <slot
                                    :name="`item.prepend.${header.key as any}`"
                                    :header
                                />
                            </td>
                        </tr>
                    </slot>
                </table>
                <table
                    v-for="(item, index) in items"
                    :key="index"
                    :class="(item as any)[itemClass]"
                    class="data-table"
                    @click="emit('click:row', item, index)"
                >
                    <tr
                        v-for="header in normalizedHeaders"
                        :key="`${index}-${header.key}`"
                        class="data-row"
                        :class="[header.align || 'center', header.columnClass]"
                    >
                        <th class="data-title">
                            <slot :name="`header.${header.key as any}`" :header>
                                {{ header.title }}
                            </slot>
                        </th>
                        <td class="data">
                            <slot
                                :name="`item.${header.key as any}`"
                                :header
                                :value="(item as any)[header.key]"
                                :item
                                :index
                            >
                                {{ (item as any)[header.key] }}
                            </slot>
                        </td>
                    </tr>
                </table>
                <table v-if="hasAppendSlot" class="data-table append">
                    <slot name="row.append">
                        <tr
                            v-for="header in appendHeaders"
                            :key="`append-${header.key}`"
                            class="data-row"
                            :class="[
                                header.align || 'center',
                                header.columnClass,
                            ]"
                        >
                            <th class="data-title">
                                <slot
                                    :name="`header.append.${header.key as any}`"
                                    :header
                                >
                                    {{ header.title }}
                                </slot>
                            </th>
                            <td class="data">
                                <slot
                                    :name="`item.append.${header.key as any}`"
                                    :header
                                />
                            </td>
                        </tr>
                    </slot>
                </table>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.center {
    text-align: center !important;
}
.start {
    text-align: start !important;
}
.end {
    text-align: end !important;
}
@include scroll-fantasy;
.mr-data-table-root {
    background-color: white;
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    &.scroll {
        overflow: auto;
        .header,
        .data {
            white-space: nowrap;
        }
    }
    &.horizontal {
        overflow-y: auto;
        @include scroll-fantasy;
        .table {
            border-spacing: 0;
            width: 100%;
            max-height: 100%;
            .row-header {
                width: 100%;
                border-radius: 8px;
                @include tg-m($w7, usePalette());
                &.fixed {
                    position: sticky;
                    top: 0;
                    z-index: 10000000;
                }

                .header {
                    &:first-child {
                        border-radius: 0 8px 8px 0;
                    }

                    &:last-child {
                        border-radius: 8px 0 0 8px;
                    }

                    padding: 4px 8px;
                    background-color: usePalette($p-cool-gray, $p-100);
                    &.sticky {
                        position: sticky;
                        right: 0;
                    }
                }
            }

            .row-data,
            .append,
            .prepend {
                width: 100%;
                &.clickable {
                    cursor: pointer;
                }
                .data {
                    padding: 8px;
                    @include tg-m($w5, usePalette($p-blue, $p-700));
                    &.bordered {
                        border: 2px solid usePalette($p-blue, $p-200);
                    }
                    &:first-child {
                        border-inline-start: none;
                    }
                    &:last-child {
                        border-inline-end: none;
                    }
                    &.sticky {
                        position: sticky;
                        right: 0;
                        background-color: white;
                    }
                }
            }

            .append,
            .last {
                > .data {
                    border-bottom: none !important;
                }
            }

            .prepend,
            .first {
                > .data {
                    border-top: none !important;
                }
            }
        }
    }
    &.vertical {
        display: flex;
        flex-direction: column;
        gap: 24px;
        .no-data {
            width: 100%;
            @include tg-m($w7, usePalette($p-blue, $p-700));
            text-align: center;
        }
        .data-table {
            width: 100%;
            .data-row {
                width: 100%;
                &:first-child .data-title {
                    border-top-right-radius: 8px;
                    border-top-left-radius: 8px;
                }
                &:last-child .data-title {
                    border-bottom-right-radius: 8px;
                    border-bottom-left-radius: 8px;
                }
                &:not(:last-child) {
                    .data,
                    .data-title {
                        border-bottom: solid 1px
                            usePalette($p-cool-gray, $p-100);
                    }
                }
                .data-title {
                    width: 45%;
                    padding: 8px;
                    @include tg-m($w5, usePalette($p-blue, $p-700));
                    background-color: usePalette($p-cool-gray, $p-100);
                }
                .data {
                    width: 55%;
                    padding: 10.5px 8px;
                    @include tg-m($w5, usePalette($p-blue, $p-700));
                }
            }
        }
    }
}
</style>
